import { recordEvent, recordPage, googleEvent } from "./analytics-tracker";
/**
 * GTM event helpers.
 *
 * All helpers guard against server-side execution so they can be imported
 * safely in isomorphic components/route files.
 */

export const EVENT_PAGE_VIEW = "page_view";
export const EVENT_CONTACT_FORM_SUBMIT = "contact_form_submit";
export const EVENT_AUDIT_CTA_CLICK = "audit_cta_click";
export const EVENT_CONTACT_PAGE_VIEW = "bekijk_contactpagina";
export const EVENT_NAV_CLICK = "nav_click";
export const EVENT_LINK_CLICK = "link_click";
export const EVENT_OUTBOUND_LINK_CLICK = "outbound_link_click";
export const EVENT_FORM_SUBMIT = "form_submit";
export const EVENT_LANGUAGE_SWITCH = "language_switch";
/**
 * Bezoeker landt op een pagina in een andere taal dan de pagina waar hij
 * vandaan kwam, zonder bewuste taalwissel. Wijst op een CTA die de
 * lokalisatie omzeilt — zichtbaar in GA4 als regressie-alarm.
 */
export const EVENT_LANGUAGE_MISMATCH = "language_mismatch";
export const EVENT_TEMPLATE_HINT_OPEN = "template_hint_open";
/** Klik op "Ander pakket kiezen" in het contactformulier (bijv. beoordeling → /audit). */
export const EVENT_PACKAGE_CHANGE_CLICK = "package_change_click";
/** Klik op "lees de volledige uitleg in de woordenlijst" vanuit een tooltip. */
export const EVENT_GLOSSARY_LINK_CLICK = "glossary_link_click";
/** Weergave van een woordenlijst-term op zijn eigen URL. */
export const EVENT_GLOSSARY_TERM_VIEW = "glossary_term_view";
/** Klik op "Link kopiëren" bij een woordenlijst-term. */
export const EVENT_GLOSSARY_TERM_COPY_LINK = "glossary_term_copy_link";
/** Succesvolle offerte-aanvraag: contactformulier verzonden mét gekozen pakket. */
export const EVENT_QUOTE_REQUEST_SUBMIT = "quote_request_submit";
/** Klik op een telefoonnummer (tel:-link). */
export const EVENT_PHONE_CLICK = "phone_click";
/** Klik op een e-mailadres (mailto:-link). */
export const EVENT_EMAIL_CLICK = "email_click";
/** Succesvolle aanvraag van het voorbeeldrapport (leadmagneet). */
export const EVENT_RAPPORT_DOWNLOAD = "rapport_download";
/** Bezoeker heeft 75% van de pagina gescrold. */
export const EVENT_SCROLL_DEPTH = "scroll_depth";
/** Klik op een primaire CTA-knop (btn-cta). */
export const EVENT_CTA_CLICK = "cta_click";
/** Bezoeker kiest een pakket op /pakketten (klik naar contact met pakket). */
export const EVENT_PACKAGE_SELECT = "package_select";

export type GtmEventParams = Record<string, unknown>;

/** Central privacy-safe dispatch; Google receives only post-consent events. */
export function pushEvent(event: string, params?: GtmEventParams) {
  recordEvent(event, params);
}
export function pushPageView(pathname: string) { recordPage(pathname); }
export function pushFormSubmit(formName: string, params?: GtmEventParams & { form_status?: "success" | "error" }) {
  recordEvent(params?.form_status === "error" ? "form_error" : "form_submit", {
    ...params, form_name: params?.form_variant === "beoordeling_sessie" ? "beoordeling_sessie" : formName,
    category: params?.form_status === "error" ? "server" : undefined,
  });
}

/**
 * Taalwissel-event. Logt vanaf welke pagina de bezoeker vertrekt en naar
 * welke gelokaliseerde URL hij navigeert.
 *
 * Parameters:
 *  - from_lang / to_lang   Taalcodes (nl | fr | en)
 *  - from_path / to_path   Volledige paden inclusief taalprefix
 *  - page_key              Canonieke pagina-sleutel uit route-slugs (bv. "pakketten")
 *  - same_page             true wanneer de bezoeker op dezelfde pagina blijft
 *                          (false = terugval naar de gelokaliseerde home)
 */
export function pushLanguageSwitch(params: {
  from_lang: string;
  to_lang: string;
  from_path: string;
  to_path: string;
  page_key?: string;
  same_page: boolean;
}) {
  if (typeof window === "undefined") return;
  googleEvent(EVENT_LANGUAGE_SWITCH, {
    ...params,
    
    page_title: document.title,
  });
}

/**
 * Tooltip met paginatypes (Local Hero / Local Hero Premium) geopend.
 *
 * Parameters:
 *  - package_tier         "premium", "standard", "pro" of "enterprise"
 *  - feature_label        Label van het feature-item waar de badge bij hoort
 *  - trigger              "click" (mobiel + desktop klik) of "hover" (desktop)
 *  - template_types       Namen van de getoonde paginatypes (array)
 *  - template_types_list  Zelfde lijst als komma-string (handig in GA4)
 *  - template_types_count Aantal getoonde paginatypes
 */
export function pushTemplateHintOpen(params: {
  package_tier: "premium" | "standard" | "enterprise" | "pro";
  feature_label: string;
  trigger: "click" | "hover";
  template_types: string[];
}) {
  if (typeof window === "undefined") return;
  googleEvent(EVENT_TEMPLATE_HINT_OPEN, {
    package_tier: params.package_tier,
    feature_label: params.feature_label,
    trigger: params.trigger,
    template_types: params.template_types,
    template_types_list: params.template_types.join(", "),
    template_types_count: params.template_types.length,
    page_path: window.location.pathname,
  });
}

/**
 * Verkeerde taallanding: bezoeker kwam van een pagina in taal A en belandde
 * op taal B zonder bewuste taalwissel. Maak in GA4 een custom event-rapport
 * op "language_mismatch" om regressies in gelokaliseerde CTA's direct te zien.
 *
 * Parameters:
 *  - from_path / to_path     Vorige en nieuwe URL-pad
 *  - expected_lang / actual_lang
 *  - link_text               Tekst van de aangeklikte link (indien bekend)
 */
/**
 * Offerte-aanvraag verzonden: contactformulier succesvol ingediend mét een
 * gekozen pakket. Hiermee ziet u in GA4 welke pagina's én welke pakketten
 * aanvragen opleveren.
 *
 * Parameters:
 *  - package_id     Slug van het pakket (bv. "local-hero-premium")
 *  - package_name   Weergavenaam van het pakket
 *  - package_price  Actieve prijs als getal (excl. btw), indien bekend
 *  - form_variant   contact | audit | agencies | beoordeling
 *  - lang           Taal waarin het formulier verzonden werd
 */
export function pushQuoteRequestSubmit(params: {
  package_id: string;
  package_name: string;
  package_price?: number | null;
  form_variant: string;
  lang: string;
  form_id?: string;
  form_name?: string;
}) {
  if (typeof window === "undefined") return;
  recordEvent(EVENT_QUOTE_REQUEST_SUBMIT, {
    ...params,
    page_path: window.location.pathname,
  });
}

export function pushLanguageMismatch(params: {
  from_path: string;
  to_path: string;
  expected_lang: string;
  actual_lang: string;
  link_text?: string;
}) {
  if (typeof window === "undefined") return;
  googleEvent(EVENT_LANGUAGE_MISMATCH, { ...params });
}
